import {
  Stack,
  Group,
  Text,
  Badge,
  Flex,
  ThemeIcon,
  Modal,
  Button,
  ThemeIconProps,
} from "@mantine/core"
import {
  DefaultListItem,
  DefaultListItemSelectable,
} from "../../../../-components/default-list/list-item"
import { ellipsify } from "../../../../utils/ellipsify"
import dayjs from "dayjs"
import { useGetAuthenticatedUser } from "../../../app/-components/use-authenticated-user"
import { useTranslation } from "react-i18next"
import { ChatAvatar } from "../ChatAvatar"
import { PersonNameWithUserNameFallback } from "./PersonNameWithUserNameFallback"
import { stripHtml } from "../../../../utils/stripHtml"
import { IconBellOff, IconStarFilled } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"
import { FeedSubscribeIcon } from "@/routes/app/com/feeds/-components/FeedSubscribeIcon"
import { ChatDto } from "@/orval/gen/model"
import {
  getChatGetChatByIdQueryKey,
  getChatGetChatsQueryKey,
  getChatUserHasArchivedChatQueryKey,
  useChatDeleteChat,
  useChatToggleInactivateChat,
  useChatToggleMuteChat,
} from "@/orval/gen"

interface Props extends DefaultListItemSelectable<ChatDto> {
  scrollContainerRef?: React.RefObject<HTMLDivElement>
  touchMenuEnabled?: boolean
}

export function ChatListItem({
  entity: chat,
  scrollContainerRef,
  touchMenuEnabled,
  ...props
}: Props) {
  const { t } = useTranslation("app_server")

  const timeStamp = chat.lastMessage?.timeStamp
    ? dayjs(chat.lastMessage.timeStamp)
    : chat.createdAt
      ? dayjs(chat.createdAt)
      : null

  const timeIndicator = timeStamp?.isToday()
    ? timeStamp.format("HH:mm")
    : timeStamp?.add(1, "day").isToday()
      ? t("Yesterday")
      : timeStamp?.format(timeStamp.add(2, "day").isToday() ? "dddd" : "L")

  const currentUserId = useGetAuthenticatedUser().data.userId
  const currentUserMember = chat.chatMembers.find(
    (cm) => cm.userId === currentUserId
  )
  const directChatPartnerId =
    chat.chatMembers.length === 2
      ? chat.chatMembers.find((cm) => cm.userId !== currentUserId)?.userId
      : chat.chatMembers[0]?.userId
  const queryClient = useQueryClient()
  const invalidateQueries = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getChatGetChatsQueryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: getChatUserHasArchivedChatQueryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: getChatGetChatByIdQueryKey({ chatId: chat.chatId }),
    })
  }, [queryClient, chat.chatId])

  const { mutateAsync: muteChat } = useChatToggleMuteChat({
    mutation: {
      onSuccess: () => {
        invalidateQueries()
      },
    },
  })
  const navigate = useNavigate()
  const { mutateAsync: inactivateChat } = useChatToggleInactivateChat({
    mutation: {
      onSuccess: () => {
        invalidateQueries()
        navigate({ to: "/app/com/chat" })
      },
    },
  })

  const { mutateAsync: deleteChat } = useChatDeleteChat({
    mutation: {
      onSuccess: () => {
        invalidateQueries()
        navigate({ to: "/app/com/chat" })
      },
    },
  })

  const [menuOpened, setMenuOpened] = React.useState(false)

  const timerRef = React.useRef<number | null>(null)

  const handleTouchStart = () => {
    if (touchMenuEnabled && chat.chatType !== "Broadcast") {
      timerRef.current = window.setTimeout(() => {
        setMenuOpened(true)
      }, 1200)
    }
  }

  const handleTouchEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef?.current
    if (!scrollContainer) return

    const handleScroll = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }

    scrollContainer.addEventListener("scroll", handleScroll)

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const [messageDraft] = useUserLocalStorage({
    key: "chat-" + chat.chatId + "-message",
    defaultValue: "",
  })

  const FeedStarIcon = React.memo(() => {
    let iconProps: ThemeIconProps = { size: "xs", variant: "subtle" }
    switch (chat.chatType) {
      case "Feed":
        if (currentUserMember?.isAdmin) {
          iconProps = { ...iconProps, color: "red" }
        } else {
          return (
            <FeedSubscribeIcon
              size={"xs"}
              subscribed={!!currentUserMember}
              id={chat.chatId}
            />
          )
        }
        break
      case "BroadcastFeed":
        if (currentUserMember?.isAdmin) {
          iconProps = {
            ...iconProps,
            color: "red",
            c: "gray",
            variant: "light",
          }
        } else {
          iconProps = { ...iconProps, color: "gray" }
        }
        break
      default:
        return null
    }
    return (
      <ThemeIcon {...iconProps}>
        <IconStarFilled />
      </ThemeIcon>
    )
  })

  const isFeed = ["Feed", "BroadcastFeed"].includes(chat.chatType)

  return (
    <>
      <DefaultListItem
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ userSelect: "none", WebkitUserSelect: "none" }}
        entity={chat}
        key={chat.chatId}
        p="0.5rem"
        bg={
          menuOpened
            ? "color-mix(in srgb, var(--mantine-color-body) 85%, var(--mantine-color-text) 15%)"
            : undefined
        }
        {...props}
      >
        <Group justify="space-between" flex={1}>
          <ChatAvatar chat={chat} />
          <Stack w="0" flex="1" gap="0.25rem">
            <Group wrap="nowrap">
              {chat.chatType === "Direct" && directChatPartnerId ? (
                <PersonNameWithUserNameFallback userId={directChatPartnerId} />
              ) : (
                <Text style={ellipsify} size="md">
                  {chat.name}
                </Text>
              )}
              <Flex flex="1" />
              {chat.chatType !== "Broadcast" && chat.isMuted && (
                <ThemeIcon variant="transparent" c="dimmed" size="xs">
                  <IconBellOff />
                </ThemeIcon>
              )}
              <Text size="xs" c="dimmed" style={{ justifySelf: "flex-end" }}>
                {timeIndicator}
              </Text>
              {!isFeed && chat.unreadMessages ? (
                <Badge size="xs" circle>
                  {chat.unreadMessages}
                </Badge>
              ) : null}
              <FeedStarIcon />
            </Group>
            <Text size="xs" style={ellipsify} c="dimmed">
              {!props.selectionState?.isSelected && messageDraft ? (
                <Text>
                  {t("Draft")}: {messageDraft}
                </Text>
              ) : chat.chatId === 0 ? (
                ""
              ) : chat.lastMessage ? (
                <>
                  {currentUserId === chat.lastMessage?.userId ? (
                    t("You")
                  ) : (
                    <Text span>
                      <PersonNameWithUserNameFallback
                        userId={chat.lastMessage.userId}
                      />
                    </Text>
                  )}
                  {`: ${isFeed ? chat.lastMessage.title : chat.lastMessage.message ? stripHtml(chat.lastMessage.message) : chat.lastMessage.fileName ? `${t("File")}: ${chat.lastMessage.fileName}` : ""}`}
                </>
              ) : (
                t("NoMessages")
              )}
            </Text>
          </Stack>
        </Group>
      </DefaultListItem>
      <Modal
        portalProps={{
          style: { userSelect: "none", WebkitUserSelect: "none" },
          onSelect: (e) => e.preventDefault(),
        }}
        title={chat.name}
        style={{ userSelect: "none", WebkitUserSelect: "none" }}
        opened={menuOpened}
        onClose={() => setMenuOpened(false)}
        withCloseButton={false}
        centered
      >
        <Stack>
          <Button
            fullWidth
            color="text"
            variant="transparent"
            onClick={async (e) => {
              e.stopPropagation()
              e.preventDefault()
              await muteChat({ params: { chatId: chat.chatId } })
            }}
          >
            {chat.isMuted ? t("Unmute") : t("Mute")}
          </Button>
          <Button
            color="text"
            fullWidth
            style={{ userSelect: "none", WebkitUserSelect: "none" }}
            variant="transparent"
            onClick={async (e) => {
              e.stopPropagation()
              e.preventDefault()
              await inactivateChat({ params: { chatId: chat.chatId } })
            }}
          >
            {chat.isInactive ? t("Unarchive") : t("Archive")}
          </Button>
          {chat.isEmpty && currentUserMember?.isAdmin && (
            <Button
              color="red"
              fullWidth
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
              variant="transparent"
              onClick={async (e) => {
                e.stopPropagation()
                e.preventDefault()
                await deleteChat({ params: { chatId: chat.chatId } })
              }}
            >
              {t("Delete")}
            </Button>
          )}
        </Stack>
      </Modal>
    </>
  )
}
